import { Component, Input, OnChanges, Output, SimpleChanges, ViewChild, EventEmitter } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';

import { FiltreGestionComponent } from './filtre-gestion/filtre-gestion.component';
import { HabilitationDto } from '../../../models/habilitation-dto.model';
import { HabilitationType } from '../../../models/habilitation-type.model';
import { IdNatPSPipe } from '../../../shared/pipes/id-nat-ps.pipe';
import { StructureDto } from '../../../models/structure-dto.model';
import { StructureRaisonIdNatPipe } from '../../../shared/pipes/structure-raison-id-nat.pipe';

@Component({
  selector: 'app-filtre',
  templateUrl: './filtre.component.html',
})
export class FiltreComponent implements OnChanges {

  @Input() data: any[];
  @Input() type: string;
  @Input() typeGestion: HabilitationType;
  @Input() callFilterOuvertes;
  @Input() callFiltrer;

  @Output() filtreEvent = new EventEmitter<any[]>();
  @Output() sendIdNationalChangeEvent = new EventEmitter<string>();

  @ViewChild('filtre') filtre: FiltreGestionComponent;

  // Définir des tableaux vides
  usages: { name: string }[] = [];
  roles: { name: string }[] = [];
  etats: { name: string }[] = [];
  offres: { name: string }[] = [];
  structures: { name: string }[] = [];

  dataFiltered: any[] = [];

  constructor(
    private readonly structureRaisonIdNat: StructureRaisonIdNatPipe,
    private readonly idNatPSPipe: IdNatPSPipe
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.data || changes.callFilterOuvertes) {
      if (this.type === 'Habilitation') {
        this.setHabilitationFiltre();
      } else if (this.type === 'Gestion') {
        this.setGestionFiltre();
      }
    }

    // force to refresh `etats` field
    if (changes.callFiltrer && !changes.callFiltrer.isFirstChange()) {
      this.setGestionFiltre();
      this.data = [...this.data];
    }
  }

  setHabilitationFiltre() {
    // Remplir des tableaux avec des données
    this.data.forEach((h) => {
      if (!this.roles.some((r) => r.name === h.roleMetier.code)) {
        this.roles.push({ name: h.roleMetier.libelleCourt ?? null });
      }

      if (!this.usages.some((r) => r.name === h.usage) && h.usage) {
        this.usages.push({ name: h.usage ?? null });
      }

      if (!this.offres.some((o) => o.name === h.typeOffre) && h.typeOffre) {
        this.offres.push({ name: h.typeOffre ?? null });
      }
    });

    this.sortData();
  }

  receiveIdNationalChange(event: string) {
    this.sendIdNationalChangeEvent.emit(event);
  }

  setGestionFiltre() {
    // Remplir des tableaux avec des données
    this.data.forEach((h) => {

      if (HabilitationType.DELEGUE_PM === this.typeGestion) {
        this.addStructuresForHabilitationDeleguePM(h);
      } else {
        this.addStructuresForHabilitationPP(h);
      }
      // checking weather array etat contain all the names
      this.checkEtats(h);

      this.addUsages(h);

      this.addOffers(h);
    });

    this.sortData();
  }

  private addOffers(habilitation: any) {
    if (!this.offres.some((o) => o.name === habilitation.typeOffre) && habilitation.typeOffre) {
      this.offres.push({ name: habilitation.typeOffre ?? null });
    }
  }

  private addUsages(habilitation: any) {
    if (!this.usages.some((u) => u.name === habilitation.usage) && habilitation.usage) {
      this.usages.push({ name: habilitation.usage ?? null });
    }
  }

  private checkEtats(habilitation: any) {
    if (!this.etats.some((e) => e.name === habilitation.etat)) {
      // adding to array because value doesnt exists
      this.etats.push({ name: habilitation.etat ?? null });
    } else {
      const arrayNamesEtat = [];
      this.data.forEach((d) => arrayNamesEtat.push(d.etat));
      this.etats.forEach((etat) => {
        if (!arrayNamesEtat.includes(etat.name)) {
          // deleting etats when nt necessary on filter
          this.etats.splice(this.etats.indexOf(etat), 1);
        }
      });
    }
  }

  private addStructuresForHabilitationPP(habilitation: HabilitationDto) {
    const raisonIdNatStructure = this.structureRaisonIdNat.transform(habilitation.structure);
    if (!this.structures.some((s) => s.name === raisonIdNatStructure)) {
      this.structures.push({ name: raisonIdNatStructure });
    }
  }

  private addStructuresForHabilitationDeleguePM(habilitation: HabilitationDto) {
    if (!this.structures.some((s) => s.name === habilitation.structure.raisonSociale)) {
      this.structures.push({ name: habilitation.structure.raisonSociale });
    }
  }

  /*
   *Trier les tableaux
   *Envoyer des données
   */
  sortData(): void {
    if (this.type === 'Gestion') {
      this.structures.sort((a, b) => a.name?.localeCompare(b.name));
      this.etats.sort((a, b) => a.name?.localeCompare(b.name));
    }
    if (this.type === 'Habilitation') {
      this.roles.sort((a, b) => a.name?.localeCompare(b.name));
    }
    this.usages.sort((a, b) => a.name?.localeCompare(b.name));
    this.offres.sort((a, b) => a.name?.localeCompare(b.name));

    if (this.filtre) {
      if (this.type === 'Gestion') {
        this.filtre.setGestionDataFilter(
          this.structures,
          this.etats,
          this.usages,
          this.offres
        );
      }
      if (this.type === 'Habilitation') {
        this.filtre.setHabilitationDataFilter(
          this.usages,
          this.roles,
          this.offres
        );
      }
    }
  }

  receiveFiltreEvent(filtreForm: UntypedFormGroup) {
    switch (this.type) {
      case 'Habilitation':
        this.filtrer(filtreForm);
        break;
      case 'Structure':
        this.filtrerStructures(filtreForm);
        break;
      case 'Gestion':
        this.filtrer(filtreForm);
        break;
      default:
        break;
    }
  }

  receiveEffacerFiltreEvent(effacer: boolean) {
    if (effacer) {
      this.filtreEvent.emit(this.data);
    }
  }

  filtrerStructures(filtreForm: UntypedFormGroup) {
    let dataFiltered = this.data;
    if (filtreForm.value.raisonSociale !== '') {
      dataFiltered = dataFiltered.filter(
        (s: StructureDto) =>
          s.raisonSociale === filtreForm.value.raisonSociale.toUpperCase()
      );
    }
    if (filtreForm.value.identifiantNational !== '') {
      dataFiltered = dataFiltered.filter(
        (s: StructureDto) =>
          `${s.typeStructure}${s.idStructure}` ===
          filtreForm.value.identifiantNational
      );
    }
    this.filtreEvent.emit(dataFiltered);
  }

  filtrer(filtreForm: UntypedFormGroup) {
    // Saisies pour nom et identifiant nation d'une personne
    this.dataFiltered = this.data;
    this.filtrerParNom(filtreForm);
    this.filtrerParIdNatPS(filtreForm);
    // Filtre pour une strucutre tiers
    this.filtrerParIdNatStructureTiers(filtreForm);
    this.filtrerParRaisonSocialeStructureTiers(filtreForm);

    this.filtrerParOffresEtUsages(filtreForm);

    if (this.type === 'Gestion') {
      this.filtrerGestion(filtreForm);
    }
    if (this.type === 'Habilitation') {
      this.filtrerHabilitation(filtreForm);
    }
    this.filtreEvent.emit(this.dataFiltered);
  }

  private filtrerParOffresEtUsages(filtreForm: UntypedFormGroup) {
    const isAdminTechFilterSelected = filtreForm?.get('roles')?.value["Administrateur technique"];
    if ([HabilitationType.CL, HabilitationType.CL_PM].includes(this.typeGestion) || isAdminTechFilterSelected) {
      // Filtrer par usage
      const usages = [];
      Object.values(filtreForm.get('usages').value as UntypedFormGroup).forEach((usage, index) => {
        if (usage) {
          usages.push(Object.keys(filtreForm.get('usages').value as UntypedFormGroup)[index]);
        }
      });
      this.dataFiltered = this.dataFiltered.filter((h: HabilitationDto) => usages.includes(h?.usage?.trim()));
      // Filtrer par offre
      const offres = [];
      Object.values(filtreForm.get('offres').value as UntypedFormGroup).forEach(
        (o, i) => {
          if (o) {
            offres.push(Object.keys(filtreForm.get('offres').value as UntypedFormGroup)[i]);
          }
        }
      );
      this.dataFiltered = this.dataFiltered.filter((h: HabilitationDto) => offres.includes(h.typeOffre));

    }
  }

  private filtrerParRaisonSocialeStructureTiers(filtreForm: UntypedFormGroup) {
    if (filtreForm.value.raisonSocialeTiers && filtreForm.value.raisonSocialeTiers !== '') {
      this.dataFiltered = this.dataFiltered.filter(
        (h: HabilitationDto) => h.structureHabilitee.raisonSociale.toUpperCase().includes(filtreForm.value.raisonSocialeTiers.toUpperCase())
      );
    }
  }

  private filtrerParIdNatStructureTiers(filtreForm: UntypedFormGroup) {
    if (filtreForm.value.idNatTiers && filtreForm.value.idNatTiers !== '') {
      this.dataFiltered = this.dataFiltered.filter(
        (h: HabilitationDto) =>
          this.structureRaisonIdNat.transform(h.structureHabilitee, false) === filtreForm.value.idNatTiers
      );
    }
  }

  private filtrerParIdNatPS(filtreForm: UntypedFormGroup) {
    if (filtreForm.value.identifiantNational && filtreForm.value.identifiantNational !== '') {
      this.dataFiltered = this.dataFiltered.filter(
        (h: HabilitationDto) =>
          this.idNatPSPipe.transform(h.personneHabilitee) === filtreForm.value.identifiantNational
      );
    }
  }

  private filtrerParNom(filtreForm: UntypedFormGroup) {
    if (filtreForm.value.nom && filtreForm.value.nom !== '') {
      this.dataFiltered = this.dataFiltered.filter(
        (h: HabilitationDto) => h.personneHabilitee.nom === filtreForm.value.nom.toUpperCase()
      );
    }
  }

  filtrerGestion(filtreForm: UntypedFormGroup) {
    // Filtrer par structures
    const structures = [];
    Object.values(filtreForm.get('structures').value as UntypedFormGroup).forEach(
      (s, i) => {
        if (s) {
          structures.push(Object.keys(filtreForm.get('structures').value as UntypedFormGroup)[i]);
        }
      }
    );

    this.dataFiltered = this.dataFiltered.filter((h: HabilitationDto) =>
      this.typeGestion === HabilitationType.DELEGUE_PM ?
        structures.includes(h.structure.raisonSociale) : structures.includes(this.structureRaisonIdNat.transform(h.structure)));


    // Filtrer par etats
    const etats = [];
    Object.values(filtreForm.get('etats').value as UntypedFormGroup).forEach(
      (e, i) => {
        if (e) {
          etats.push(
            Object.keys(filtreForm.get('etats').value as UntypedFormGroup)[i]
          );
        }
      }
    );

    this.dataFiltered = this.dataFiltered.filter((h: HabilitationDto) => etats.includes(h.etat));

  }

  filtrerHabilitation(filtreForm: UntypedFormGroup) {
    // Filtrer par roles
    const roles = [];
    if (filtreForm?.get('roles')?.value) {
      Object.values(filtreForm.get('roles').value as UntypedFormGroup).forEach(
        (r, i) => {
          if (r) {
            roles.push(
              Object.keys(filtreForm.get('roles').value as UntypedFormGroup)[i]
            );
          }
        }
      );
    }

    if (roles.length > 0) {
      this.dataFiltered = this.dataFiltered.filter((h: HabilitationDto) =>
        roles.includes(h.roleMetier.libelleCourt)
      );
    }
  }

}
